import React, { Fragment } from "react";

import PropTypes from "prop-types";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import withStyles from "@material-ui/core/styles/withStyles";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import ListItemText from "@material-ui/core/ListItemText";
import Collapse from "@material-ui/core/Collapse";
import ListItemIcon from "@material-ui/core/ListItemIcon";
import Icon from "../Icon";
import ShortMenu from "./shortMenu";
import "./tooltip.css";

const styles = theme => ({
  listItem: {
    padding: "16px !important",
    lineHeight: 1,
    cursor: "pointer",
    "&:hover span": {
      color: theme.palette.hylyBlue
    },
    "&:hover i": {
      color: theme.palette.hylyBlue
    },
    "&:hover svg": {
      stroke: theme.palette.hylyBlue
    },
    "&:hover path": {
      stroke: theme.palette.hylyBlue
    },
    "&:hover div": {
      color: theme.palette.hylyBlue
    },
    color: "black"
  },
  listItemSelected: {
    padding: "16px !important",
    lineHeight: 1,
    cursor: "pointer",
    "&:hover span": {
      color: theme.palette.hylyBlue
    },
    "&:hover div": {
      color: theme.palette.hylyBlue
    },
    "& i": {
      color: theme.palette.hylyBlue
    },
    "& svg": {
      stroke: theme.palette.hylyBlue
    },
    "& path": {
      stroke: theme.palette.hylyBlue
    },
    backgroundColor: "#f4fbfd !important"
  },
  subItemSelected: {
    backgroundColor: "#f4fbfd !important"
  },
  nested: {
    paddingLeft: 0,
  },
  textItem: {
    color: theme.palette.darkBlack,
    fontWeight: theme.fontWeight.main,
    textOverflow: "ellipsis !important",
    overflow: "hidden",
    whiteSpace: "nowrap",
  },
  nestedText: {
    color: "rgba(51, 51, 51, 0.7)",
    fontWeight: theme.fontWeight.main,
    cursor: "pointer",
    textOverflow: "ellipsis",
    overflow: "hidden",
    whiteSpace: "nowrap"
  },
  remove_padding: {
    paddingLeft: "5px !important",
    marginTop: 2
  },
  selected: {
    fontWeight: theme.fontWeight.bold,
    color: "#333333"
  },
  list: {
    paddingBottom: 0,
  },
  icon_class_container: {
    margin: `0 0 4px 0`,
    minWidth: 36
  },
  drop_down_icon_open: {
    webkitTransform: "rotate(90deg)",
    transform: "rotate(90deg)"
  },
  drop_down_icon_close: {
    webkitTransform: "rotate(-90deg)",
    transform: "rotate(-90deg)"
  },
  drop_down_icon_normal: {
    webkitTransform: "rotate(0deg)",
    transform: "rotate(0deg)"
  },
  drop_down_icon_left: {
    webkitTransform: "rotate(-180deg)",
    transform: "rotate(-180deg)"
  },
  submenu: {
    marginLeft: 63,
  },
  subMenuItem: {
    cursor: "pointer",
    "&:hover span": {
      color: theme.palette.hylyBlue
    },
    "&:hover div": {
      color: theme.palette.hylyBlue
    },
    "& i": {
      color: theme.palette.hylyBlue
    },
    "&:hover svg": {
      stroke: theme.palette.hylyBlue
    },
    "&:hover path": {
      stroke: theme.palette.hylyBlue
    },
    backgroundColor: theme.palette.white,
    padding: "10px 20px;"
  },
  selectedSubMenuItem: {
    color: `${theme.palette.hylyBlue} !important;`
  },
  subMenuItemText: {
    paddingLeft: "0 !important",
    color: theme.palette.darkBlack,
    marginTop: "0px !important",
    marginBottom: "0px !important",
    marginLeft: "5px !important"
  },
  submenuPopover: {
    boxShadow: "none !important",
    backgroundColor: theme.palette.white,
    minWidth: 180,
    left: "17px !important"
  },
  textStyle: {
    fontSize: 16,
    fontFamily: "Open Sans, arial, sans-serif",
    lineHeight: 1.5,
    width: "100%"
  },
  iconTypeAlign: {
    marginLeft: 5
  },
  textHidden: {
    display: "none"
  },
  displayIcon: {
    display: "flex",
    alignItems: "end",
    marginLeft: 36,
    height: 30
  },
  displayIconPopUp: {
    display: "flex",
    alignItems: "end",
    height: 30
  },
  subItemSelectedSVG: {
    "& svg": {
      stroke: theme.palette.hylyBlue
    },
    "& path": {
      stroke: theme.palette.hylyBlue
    },
  }
});

const NavListWithCollapse = ({
  classes: {
    icon_class_container,
    listItem,
    listItemSelected,
    subItemSelected,
    subMenuItem,
    selectedSubMenuItem,
    subMenuItemText,
    submenuPopover,
    nested,
    textItem,
    nestedText,
    selected,
    list,
    drop_down_icon_open,
    drop_down_icon_close,
    drop_down_icon_normal,
    drop_down_icon_left,
    remove_padding,
    submenu,
    textStyle,
    iconTypeAlign,
    textHidden,
    displayIcon,
    subItemSelectedSVG,
    displayIconPopUp
  },
  link_component,
  is_online,
  drop_down_icon,
  menu_open,
  menu,
  pathname,
  onClose,
  swipeable,
  ...other
}) => {
  const [data_open, set_data] = React.useState({});
  const [currentTarget, setCurrentTarget] = React.useState({});
  const matchesSwipeable = useMediaQuery("(min-width:1280px)");
  React.useEffect(() => {
    if (!menu_open) {
      setCurrentTarget({})
    }
  }, [menu_open])

  const is_parent = children => Boolean(children) && Boolean(children.length);
  const create_path = (path = "/") =>
    Boolean(path) && /^\//.test(path) ? path : Boolean(path) ? "/" + path : "/";

  const open_data = (property, e) => {
    set_data({ ...data_open, [property]: !data_open[property] });
    setCurrentTarget({ ...currentTarget, [property]: e.currentTarget })
  }

  const clear_target = () => {
    setCurrentTarget({})
  }

  const check_if_selected = (children, path, pathname) => {
    if (!Boolean(children && children.length)) {
      switch (typeof path) {
        case "string":
          return path === "/"
            ? pathname === path
            : new RegExp(`^/?${path}$`).test(pathname);
        case "object":
          if (Array.isArray(path)) {
            return pathname === "/"
              ? path.includes(pathname)
              : Boolean(path.find(o => new RegExp(`^/?${o}$`).test(pathname)));
          }
          console.error(
            `Expected path prop to be either a string, an array or function but received ${typeof path}`
          );
          return false;
        case "function":
          return path(pathname, children);
        default:
          console.error(
            `Expected path prop to be either a string, an array or function but received ${typeof path}`
          );
          return false;
      }
    }
  };

  const check_if_child_selecetd = (children) => {
    if (!children) return false;
    for (let i = 0; i < children.length; i++) {
      const data_selected = check_if_selected(
        children[i].children,
        children[i].path,
        pathname
      );
      if (data_selected) return true;
    }
    return false
  }


  return (
    <List className={list}>
      {Boolean(menu && menu.length)
        ? menu.map(
          (
            {
              to: parent_to,
              path: parent_path,
              icon_type,
              title,
              disabled,
              disabled_when_offline,
              children
            },
            index
          ) => {
            const child_selected = check_if_child_selecetd(children);
            const parent_selected = check_if_selected(
              children,
              parent_path,
              pathname
            ) || child_selected


            return (
              <Fragment key={title + index}>
                <ListItem
                  className="n-drawer-tooltip"
                  selected={parent_selected}
                  component={is_parent(children) ? "div" : link_component}
                  to={create_path(parent_to)}
                  onClick={e => {
                    return is_parent(children) &&
                      Boolean(is_online || !disabled_when_offline)
                      ? open_data(title, e)
                      : swipeable ? onClose(e) : null
                  }}
                  classes={{
                    root: listItem,
                    selected: listItemSelected
                  }}
                >

                  <ListItemIcon className={icon_class_container}>
                    <div className="n-icon-container n-drawer-tooltip">
                      <span className={`${(!matchesSwipeable || menu_open) ? 'tooltiptext-hide' : 'tooltiptext'}`}>{title}</span>
                      {
                        typeof icon_type !== "string" ? <div className={`${!menu_open && iconTypeAlign}`}>{icon_type}</div> :

                          icon_type.split(" ").length > 1 ? (
                            <Fragment>
                              <Icon type={icon_type.split(" ")[0]} size="lg" style={{ color: parent_selected ? "#26bbed" : "#333333", margin: menu_open ? 0 : 8 }} />
                              <Icon
                                type={icon_type.split(" ")[1]}
                                style={{
                                  position: "absolute",
                                  left: 120,
                                  zIndex: 1000,
                                  fontSize: 26,
                                  color: parent_selected ? "#26bbed" : "#333333"
                                }}

                              />
                            </Fragment>
                          ) : (

                              <Icon type={icon_type} size="lg" style={{ color: parent_selected ? "#26bbed" : "#333333", margin: menu_open ? 0 : 8 }} />
                            )}
                    </div>
                  </ListItemIcon>
                  <div className={`${textStyle} ${parent_selected && !child_selected ? selected : textItem} ${(!matchesSwipeable && !menu_open) && textHidden}`}>
                    {swipeable ? title : menu_open ? parent_selected ? title : title : ""}
                  </div>

                  {is_parent(children) && (
                    <Icon
                      size="sm"
                      type={drop_down_icon}
                      className={menu_open ?
                        data_open[title]
                          ? drop_down_icon_close
                          : drop_down_icon_open :
                        currentTarget[title] ? drop_down_icon_left : drop_down_icon_normal
                      }
                    />
                  )}
                </ListItem>
                {
                  is_parent(children) && (
                    <>
                      <Collapse
                        in={menu_open && data_open[title]}
                        timeout="auto"
                        unmountOnExit
                      >
                        <List component="div" disablePadding>
                          {children.map(
                            ({ to, path, title, disabled, icon_type, children }, index) => {
                              const data_selected = check_if_selected(
                                children,
                                path,
                                pathname
                              );

                              return (
                                <ListItem
                                  key={title}
                                  selected={data_selected}
                                  component={link_component}
                                  to={create_path(to || parent_to)}
                                  onClick={e => menu_open && swipeable ? onClose(e) : null}
                                  classes={{
                                    root: subMenuItem + " " + (parent_selected ? " " + subItemSelected : " "),
                                    selected: data_selected ? " " + subItemSelectedSVG : " "
                                  }}
                                >
                                  <div className={displayIcon}>
                                    {typeof icon_type !== "string" ? <div >{icon_type}</div> : null}
                                    <ListItemText
                                      inset
                                      primary={title}
                                      classes={{
                                        root: remove_padding,
                                        primary:
                                          nestedText +
                                          (data_selected ? " " + selected : "")
                                      }}
                                      style={{
                                        whiteSpace: "nowrap",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis"
                                      }}
                                    />
                                  </div>
                                </ListItem>
                              );
                            }
                          )}
                        </List>
                      </Collapse>
                      <ShortMenu open={Boolean(!menu_open && currentTarget[title] && !swipeable)} classes={submenu} popoverClasses={{ paper: submenuPopover }} anchorEl={currentTarget[title]} handleClose={clear_target.bind(this, title)}>
                        <List component="div" disablePadding>
                          {children.map(
                            ({ to, path, title, disabled, icon_type, children }, index) => {
                              const data_selected = check_if_selected(
                                children,
                                path,
                                pathname
                              );

                              return (
                                <ListItem
                                  key={title}
                                  component={link_component}
                                  to={create_path(to || parent_to)}
                                  onClick={clear_target}
                                  classes={{
                                    root: subMenuItem
                                  }}
                                >
                                  <div className={displayIconPopUp}>
                                    {typeof icon_type !== "string" ? <div >{icon_type}</div> : null}
                                    <ListItemText
                                      inset
                                      primary={title}
                                      classes={{
                                        root: subMenuItemText,
                                        primary:
                                          subMenuItemText +
                                          (data_selected ? " " + selectedSubMenuItem : "")
                                      }}
                                      style={{
                                        whiteSpace: "nowrap",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis"
                                      }}
                                    />
                                  </div>
                                </ListItem>
                              );
                            }
                          )}
                        </List>
                      </ShortMenu>
                    </>
                  )
                }
              </Fragment>
            );
          }
        )
        : null}
    </List>
  );
};


NavListWithCollapse.propTypes = {
  classes: PropTypes.shape({
    icon_class_container: PropTypes.string.isRequired,
    listItem: PropTypes.string.isRequired,
    nested: PropTypes.string.isRequired,
    textItem: PropTypes.string.isRequired,
    nestedText: PropTypes.string.isRequired,
    selected: PropTypes.string.isRequired,
    list: PropTypes.string.isRequired
  }),

  link_component: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),

  is_online: PropTypes.bool,

  drop_down_icon: PropTypes.string,

  menu_open: PropTypes.bool,

  menu: PropTypes.arrayOf(
    PropTypes.shape({
      to: PropTypes.string,
      path: PropTypes.oneOfType([
        PropTypes.func,
        PropTypes.string,
        PropTypes.array
      ]),
      icon_type: PropTypes.string,
      title: PropTypes.string,
      disabled: PropTypes.bool,
      disabled_when_offline: PropTypes.bool,
      children: PropTypes.arrayOf(
        PropTypes.shape({
          to: PropTypes.string,
          path: PropTypes.oneOfType([
            PropTypes.func,
            PropTypes.string,
            PropTypes.array
          ]),
          title: PropTypes.string,
          disabled: PropTypes.bool,
          disabled_when_offline: PropTypes.bool
        })
      )
    })
  ),

  onClose: PropTypes.func.isRequired,

  pathname: PropTypes.string.isRequired
};

NavListWithCollapse.defaultProps = {
  link_component: "div",

  is_online: true,

  drop_down_icon: "icon-right-arrow",

  menu_open: false
};

export default withStyles(styles)(NavListWithCollapse);
